import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {useQueryClient} from 'react-query'
import {io} from 'socket.io-client'
import {AuthContext} from 'contexts/AuthContext'
import {TabsContext} from 'contexts/TabsContext'
import {getToken} from 'api'
import {inboxBadgeCount} from 'utils/inboxBadge'

export const RealtimeContext = createContext(null)

const TYPING_TTL_MS = 4000

/**
 * Socket.io client for Circles/DM realtime. The socket is an accelerant,
 * not the source of truth: server events are lightweight "conversation X
 * changed" signals and the client refetches through the same REST queries
 * polling uses — so a dropped socket degrades to polling, never to lost
 * messages. Typing and presence are the only purely-ephemeral streams.
 */
export const RealtimeProvider = ({children}) => {
  const {state} = useContext(AuthContext)
  const {setUnreadChats} = useContext(TabsContext)
  const queryClient = useQueryClient()

  const socketRef = useRef(null)
  const [isConnected, setIsConnected] = useState(false)
  // conversationId -> {userId: {fullName, at}}
  const [typing, setTyping] = useState({})
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set())

  const isAuth = Boolean(state?.isAuth || getToken()?.token)

  useEffect(() => {
    if (!isAuth) return undefined
    const {token} = getToken()
    if (!token) return undefined

    const apiUrl = process.env.REACT_APP_STAGING_API_URL?.replace(/"/g, '')
    const socket = io(apiUrl, {
      auth: {token},
      transports: ['websocket', 'polling'],
    })
    socketRef.current = socket

    const refreshUnread = async () => {
      const {getConversationsCount} = await import('api')
      getConversationsCount()
        .then((counts) => setUnreadChats(inboxBadgeCount(counts)))
        .catch(() => {})
    }

    socket.on('connect', () => setIsConnected(true))
    socket.on('disconnect', () => setIsConnected(false))

    socket.on('conversation:updated', ({conversationId}) => {
      queryClient.invalidateQueries(['messages', conversationId])
      queryClient.invalidateQueries('conversations')
      refreshUnread()
    })

    socket.on('conversation:new', ({conversationId}) => {
      socket.emit('conversation:subscribe', conversationId)
      queryClient.invalidateQueries('conversations')
      refreshUnread()
    })

    // A new bell notification for this user; the Navbar refreshes on it.
    socket.on('notification:new', () => {
      window.dispatchEvent(new Event('sharpenn:notification'))
    })

    socket.on('conversation:removed', () => {
      queryClient.invalidateQueries('conversations')
    })

    socket.on('typing', ({conversationId, userId, fullName, isTyping}) => {
      setTyping((current) => {
        const inConversation = {...(current[conversationId] || {})}
        if (isTyping) {
          inConversation[userId] = {fullName, at: Date.now()}
        } else {
          delete inConversation[userId]
        }
        return {...current, [conversationId]: inConversation}
      })
    })

    socket.on('presence:change', ({userId, online}) => {
      setOnlineUserIds((current) => {
        const next = new Set(current)
        if (online) next.add(userId)
        else next.delete(userId)
        return next
      })
    })

    // Expire stale typing entries (a peer that navigated away mid-type
    // never sends isTyping:false).
    const sweep = setInterval(() => {
      setTyping((current) => {
        let changed = false
        const next = {}
        for (const [conversationId, users] of Object.entries(current)) {
          const kept = Object.fromEntries(
            Object.entries(users).filter(
              ([, entry]) => Date.now() - entry.at < TYPING_TTL_MS
            )
          )
          if (Object.keys(kept).length !== Object.keys(users).length)
            changed = true
          next[conversationId] = kept
        }
        return changed ? next : current
      })
    }, 2000)

    return () => {
      clearInterval(sweep)
      socket.disconnect()
      socketRef.current = null
      setIsConnected(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth])

  const emitTyping = useCallback((conversationId, isTyping) => {
    socketRef.current?.emit('typing', {conversationId, isTyping})
  }, [])

  const subscribeConversation = useCallback((conversationId) => {
    socketRef.current?.emit('conversation:subscribe', conversationId)
  }, [])

  const fetchOnline = useCallback(
    (conversationId) =>
      new Promise((resolve) => {
        const socket = socketRef.current
        if (!socket?.connected) return resolve([])
        socket
          .timeout(3000)
          .emit('presence:who', conversationId, (err, online) =>
            resolve(err ? [] : online || [])
          )
      }),
    []
  )

  return (
    <RealtimeContext.Provider
      value={{
        isConnected,
        typing,
        onlineUserIds,
        emitTyping,
        subscribeConversation,
        fetchOnline,
      }}
    >
      {children}
    </RealtimeContext.Provider>
  )
}

export const useRealtime = () => {
  const value = useContext(RealtimeContext)
  // Pages render outside the provider in tests — degrade to no-ops.
  return (
    value || {
      isConnected: false,
      typing: {},
      onlineUserIds: new Set(),
      emitTyping: () => {},
      subscribeConversation: () => {},
      fetchOnline: async () => [],
    }
  )
}
